import { useState, useMemo, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Typography, Box, Select, MenuItem, FormControl } from '@mui/material';
import './UserTeacherLineChart.css';
import { axiosInstance } from '../../../../../utils/tokenUtils';

const timeOptions = [
  { value: 'today', label: 'Today' },
  { value: '1_week', label: '1 Week' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '1_year', label: '1 Year' },
];

const shimmerStyle = {
  width: '100%',
  height: 300,
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
  borderRadius: 8,
};

const UserTeacherLineChart = ({ title = 'User Growth Chart' }) => {
  const [timeFilter, setTimeFilter] = useState('1_year');
  const [loading, setLoading] = useState(true);
  const [apiData, setApiData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch data when component mounts or timeFilter changes
  useEffect(() => {
    let isMounted = true;

    const fetchDataAsync = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.get(
          `/admin/dashboard/user-growth-chart?period=${timeFilter}`
        );
        if (isMounted) {
          setApiData(response.data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch data');
          console.error('Error fetching chart data:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDataAsync();

    return () => {
      isMounted = false;
    };
  }, [timeFilter]);

  // Handle filter change
  const handleFilterChange = event => {
    setTimeFilter(event.target.value);
  };

  // Transform API data to chart format
  const chartData = useMemo(() => {
    if (!apiData || !apiData.data_points) return [];

    return apiData.data_points.map(point => ({
      name: point.period,
      registrationToBooking: point.registration_booking_ratio || 0,
      bookingToAvailability: point.booking_availability_ratio || 0,
      registrations: point.registrations || 0,
      bookings: point.bookings || 0,
      availabilitySlots: point.availability_slots || 0,
    }));
  }, [apiData]);

  // Both ratios arrive from the API already in percent (0.8 means 0.8%), so
  // scale the axis to the real values. It used to treat values <= 1 as
  // decimals and stretch the axis to ~100%, flattening small lines.
  const { maxValue, yAxisTicks } = useMemo(() => {
    const max = Math.max(
      0,
      ...chartData.map(item => item.registrationToBooking),
      ...chartData.map(item => item.bookingToAvailability)
    );
    // No data yet: a plain 0-5% axis
    if (max <= 0) return { maxValue: 5, yAxisTicks: [0, 1, 2, 3, 4, 5] };

    // About 5 ticks with a "nice" step (1, 2, 2.5 or 5 x a power of 10),
    // leaving a little headroom above the highest point.
    const target = max * 1.1;
    const rawStep = target / 5;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const step = [1, 2, 2.5, 5, 10].map(m => m * magnitude).find(m => m >= rawStep);
    const top = step * Math.ceil(target / step);
    const ticks = [];
    for (let i = 0; i * step <= top + step / 1000; i += 1) {
      ticks.push(Number((i * step).toFixed(4)));
    }
    return { maxValue: top, yAxisTicks: ticks };
  }, [chartData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="user-teacher-custom-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="teacher">{`Registration to Booking Ratio: ${data.registrationToBooking.toFixed(2)}%`}</p>
          <p className="student">{`Booking to Availability Ratio: ${data.bookingToAvailability.toFixed(2)}%`}</p>
          <p className="details">{`Registrations: ${data.registrations}`}</p>
          <p className="details">{`Bookings: ${data.bookings}`}</p>
          <p className="details">{`Availability Slots: ${data.availabilitySlots}`}</p>
        </div>
      );
    }
    return null;
  };

  if (error) {
    return (
      <div className="user-teacher-analytics-container">
        <div className="user-teacher-header">
          <Typography className="user-teacher-header-title">{title}</Typography>
        </div>
        <div className="user-teacher-chart-container">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              color: '#666',
              fontSize: '14px',
            }}
          >
            Error loading chart data: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-teacher-analytics-container">
      <div className="user-teacher-header">
        <Typography className="user-teacher-header-title">{title}</Typography>
        <div className="user-teacher-time-filters">
          <FormControl variant="outlined" size="small">
            <Select
              value={timeFilter}
              onChange={handleFilterChange}
              className="time-filter-dropdown"
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: '14px',
                      fontFamily: 'Poppins',
                    },
                  },
                },
              }}
            >
              {timeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
      <div className="user-teacher-chart-container">
        {loading ? (
          <div style={shimmerStyle} />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 10 }}>
              <defs>
                <linearGradient id="colorRegistrationToBooking" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1a73e8" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#1a73e8" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="colorBookingToAvailability" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 12 }}
                // Always a percentage, including above 100% (it used to drop the %)
                tickFormatter={value => `${Number(value.toFixed(2)).toLocaleString()}%`}
                domain={[0, maxValue]}
                ticks={yAxisTicks}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="registrationToBooking"
                stroke="#1a73e8"
                strokeWidth={2}
                fill="url(#colorRegistrationToBooking)"
                fillOpacity={1}
              />
              <Area
                type="monotone"
                dataKey="bookingToAvailability"
                stroke="transparent"
                fill="url(#colorBookingToAvailability)"
                fillOpacity={1}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
        <div className="user-teacher-legend">
          <div className="user-teacher-legend-item">
            <div className="user-teacher-legend-color registration"></div>
            <span>Registration to Booking Ratio</span>
          </div>
          <div className="user-teacher-legend-item">
            <div className="user-teacher-legend-color booking"></div>
            <span>Booking to Availability Ratio</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTeacherLineChart;
